import React, { useState, useEffect, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { v4 as uuidv4 } from "uuid";
import { database } from "../firebase";
import AltEntriesHeader from "../components/alt-entries/AltEntriesHeader";
import AltEntriesFilterCard from "../components/alt-entries/AltEntriesFilterCard";
import AltEntriesKpiGrid from "../components/alt-entries/AltEntriesKpiGrid";
import AltEntriesCardList from "../components/alt-entries/AltEntriesCardList";
import AltEntriesFloatingBar from "../components/alt-entries/AltEntriesFloatingBar";
import AltEntriesClearModal from "../components/alt-entries/AltEntriesClearModal";

// Chunk array into groups of max 450 items to respect Firestore batch limit (500)
function chunkArray(arr, size) {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

const AltEntries = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const [viewBy, setViewBy] = useState("all"); // "all", "shop", "customer"
  const [modalVisible, setModalVisible] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [checkedIds, setCheckedIds] = useState(new Set());

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(false);

      const entriesSnap = await database
        .collection("alt_entries")
        .orderBy("created", "desc")
        .get();

      const entryDocs = entriesSnap.docs.map((d) => ({
        _id: d.id,
        recordType: "entry",
        ...d.data(),
      }));

      setEntries(entryDocs);

      // Auto-populate checkedIds for uncleared entries
      const initial = new Set();
      entryDocs.forEach((doc) => {
        if (!doc.isCleared) {
          initial.add(doc._id);
        }
      });
      setCheckedIds(initial);
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filter out cleared entries — New Entries page only shows active/uncleared entries (NO deductions)
  const activeEntries = useMemo(() => {
    return entries.filter((doc) => !doc.isCleared);
  }, [entries]);

  const combinedList = useMemo(() => {
    const list = [...activeEntries];
    list.sort((a, b) => {
      const timeA = new Date(a.created || a.createdAt || 0).getTime();
      const timeB = new Date(b.created || b.createdAt || 0).getTime();
      return timeB - timeA;
    });
    return list;
  }, [activeEntries]);

  const filteredItems = useMemo(() => {
    let list = combinedList;

    if (viewBy === "entries") {
      list = list.filter((doc) => doc.recordType === "entry");
    } else if (viewBy === "shop") {
      list = list.filter((doc) => doc.recordType === "entry" && doc.category === "shop");
    } else if (viewBy === "customer") {
      list = list.filter((doc) => doc.recordType === "entry" && doc.category === "customer");
    }

    if (search.trim() !== "") {
      const regex = new RegExp(search.trim(), "i");
      list = list.filter(
        (doc) =>
          regex.test(doc.phone || "") ||
          regex.test(doc.customerPhone || "") ||
          regex.test(doc.customer || "") ||
          regex.test(doc.issue || "") ||
          regex.test(doc.category || "") ||
          regex.test(doc.note || "") ||
          regex.test(doc.clearBatchId || ""),
      );
    }

    return list;
  }, [combinedList, search, viewBy]);

  // KPI Stats computed from checked items only
  const kpiStats = useMemo(() => {
    let shopTotal = 0;
    let sunilC = 0;
    let customerTotal = 0;

    filteredItems.forEach((doc) => {
      if (!checkedIds.has(doc._id)) return;

      const q = Number(doc.qty || 1);
      if (doc.category === "shop") {
        shopTotal += Number(doc.amount || 0) * q;
      } else if (doc.category === "customer") {
        sunilC += Number(doc.amount || 0) * q;
      }
      customerTotal += Number(doc.customerAmount || 0) * q;
    });

    return {
      shopTotal,
      sunilC,
      customerTotal,
      profit: customerTotal - sunilC,
    };
  }, [filteredItems, checkedIds]);

  // Count of clearable entries: checked AND not already cleared AND is entry
  const clearableCount = useMemo(() => {
    return filteredItems.filter(
      (doc) =>
        doc.recordType === "entry" && checkedIds.has(doc._id) && !doc.isCleared,
    ).length;
  }, [filteredItems, checkedIds]);

  // Toggle single item
  const handleToggle = useCallback((id) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Toggle all visible items
  const handleToggleAll = useCallback(
    (checked) => {
      setCheckedIds((prev) => {
        const next = new Set(prev);
        filteredItems.forEach((doc) => {
          if (checked) {
            next.add(doc._id);
          } else {
            next.delete(doc._id);
          }
        });
        return next;
      });
    },
    [filteredItems],
  );

  // Compute allCheckedState for Select All checkbox
  const allCheckedState = useMemo(() => {
    if (filteredItems.length === 0) return false;
    const allChecked = filteredItems.every((doc) => checkedIds.has(doc._id));
    const noneChecked = filteredItems.every((doc) => !checkedIds.has(doc._id));
    if (allChecked) return true;
    if (noneChecked) return false;
    return "indeterminate";
  }, [filteredItems, checkedIds]);

  // Clear selected entries mutation
  const handleClearAll = async ({ note }) => {
    try {
      setClearing(true);

      const clearBatchId = uuidv4();
      const clearedAt = new Date().toISOString();

      const entriesToClear = entries.filter(
        (doc) => checkedIds.has(doc._id) && !doc.isCleared,
      );

      if (entriesToClear.length === 0) {
        alert("No clearable entries selected.");
        return;
      }

      // Chunk into batches of 450 items max
      const chunks = chunkArray(entriesToClear, 450);

      for (const chunk of chunks) {
        const batch = database.batch();
        chunk.forEach((entry) => {
          const ref = database.collection("alt_entries").doc(entry._id);
          batch.update(ref, {
            isCleared: true,
            clearId: uuidv4(),
            clearBatchId,
            clearedAt,
            clearedBy: "legacy-client",
          });
        });
        await batch.commit();
      }

      // Write summary deduction record
      const newDeductionRef = database.collection("alt_deductions").doc();
      await newDeductionRef.set({
        _id: newDeductionRef.id,
        type: "deduction",
        amount: kpiStats.shopTotal + kpiStats.sunilC,
        clearedEntriesCount: entriesToClear.length,
        clearedShopTotal: kpiStats.shopTotal,
        clearedSunilC: kpiStats.sunilC,
        clearedCustomerTotal: kpiStats.customerTotal,
        clearedProfit: kpiStats.profit,
        note: (note || "").trim(),
        clearBatchId,
        createdAt: clearedAt,
        createdBy: "legacy-client",
      });

      setModalVisible(false);
      await fetchData();
    } catch (err) {
      console.error("Error clearing entries:", err);
      alert("Failed to clear entries: " + (err?.message || err));
    } finally {
      setClearing(false);
    }
  };

  return (
    <motion.div style={{ paddingBottom: "90px" }}>
      {/* Header (No export button) */}
      <AltEntriesHeader title="New Entries" />

      {/* Top Filter Controls Card */}
      <AltEntriesFilterCard
        search={search}
        onSearchChange={setSearch}
        viewBy={viewBy}
        onViewByChange={setViewBy}
        allChecked={allCheckedState}
        onToggleAll={handleToggleAll}
        showSubs={false}
      />

      {/* 4 KPI Summary Grid Cards */}
      <AltEntriesKpiGrid kpiStats={kpiStats} />

      {/* Card List */}
      <AltEntriesCardList
        loading={loading}
        error={error}
        filteredItems={filteredItems}
        viewBy={viewBy}
        checkedIds={checkedIds}
        onToggle={handleToggle}
      />

      {/* Floating Bottom Bar */}
      <AltEntriesFloatingBar
        clearableCount={clearableCount}
        clearing={clearing}
        onOpenModal={() => setModalVisible(true)}
      />

      {/* Confirmation Modal */}
      <AltEntriesClearModal
        visible={modalVisible}
        kpiStats={kpiStats}
        clearableCount={clearableCount}
        clearing={clearing}
        onClose={() => setModalVisible(false)}
        onConfirm={handleClearAll}
      />
    </motion.div>
  );
};

export default AltEntries;
